import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getDevis, getClients, createDevis, deleteDevis } from '../services/api';
import { 
  FileText, 
  Plus, 
  Search, 
  Trash2, 
  Eye,
  Filter,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const DevisListPage = () => {
  const navigate = useNavigate();
  const [devisList, setDevisList] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [newDevis, setNewDevis] = useState({
    client_id: '',
    objet: '',
    date_remise: '',
    version: '1'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [devisRes, clientsRes] = await Promise.all([
        getDevis(),
        getClients()
      ]);
      setDevisList(devisRes.data);
      setClients(clientsRes.data);
    } catch (e) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDevis = async (e) => {
    e.preventDefault();
    try {
      const res = await createDevis(newDevis);
      toast.success('Devis créé avec succès');
      setShowModal(false);
      navigate(`/devis/${res.data.id}`);
    } catch (e) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleDeleteDevis = async (id, objet) => {
    if (!window.confirm(`Supprimer le devis "${objet}" ?`)) return;
    try {
      await deleteDevis(id);
      toast.success('Devis supprimé');
      loadData();
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatMoney = (value) => {
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(value);
  };

  const filteredDevis = devisList.filter(d => {
    const matchSearch = d.objet.toLowerCase().includes(search.toLowerCase()) ||
                       d.client_nom.toLowerCase().includes(search.toLowerCase());
    const matchClient = !filterClient || d.client_id === filterClient;
    return matchSearch && matchClient;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="devis-list-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Devis</h1>
          <p className="text-slate-500 mt-1">{devisList.length} devis enregistré(s)</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          data-testid="new-devis-btn"
          className="flex items-center px-4 py-2 bg-orange-500 text-white rounded-md font-medium hover:bg-orange-600 transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau devis
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            data-testid="search-devis-input"
            type="text"
            placeholder="Rechercher un devis..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-10 pl-10 pr-4 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <select
            data-testid="filter-client-select"
            value={filterClient}
            onChange={(e) => setFilterClient(e.target.value)}
            className="h-10 pl-9 pr-8 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 appearance-none"
          >
            <option value="">Tous les clients</option>
            {clients.map(c => (
              <option key={c.id} value={c.id}>{c.nom}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Devis table */}
      {filteredDevis.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
          <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p className="text-slate-500">
            {search || filterClient ? 'Aucun devis trouvé' : 'Aucun devis créé'}
          </p>
          {!search && !filterClient && (
            <button
              onClick={() => setShowModal(true)}
              className="inline-flex items-center mt-4 text-orange-500 hover:text-orange-600"
            >
              <Plus className="w-4 h-4 mr-1" /> Créer votre premier devis
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Client</th>
                <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Objet</th>
                <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Version</th>
                <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Date</th>
                <th className="px-6 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Total HT</th>
                <th className="px-6 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Total TTC</th>
                <th className="px-6 py-3 text-center text-xs font-bold uppercase tracking-wider text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredDevis.map((d) => (
                <tr key={d.id} className="hover:bg-slate-50/50 transition-colors" data-testid={`devis-row-${d.id}`}>
                  <td className="px-6 py-4 text-sm font-medium text-slate-900">
                    <Link to={`/clients/${d.client_id}`} className="hover:text-orange-500">
                      {d.client_nom}
                    </Link>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    <Link to={`/devis/${d.id}`} className="hover:text-orange-500">
                      {d.objet}
                    </Link>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-500">v{d.version}</td>
                  <td className="px-6 py-4 text-sm text-slate-500">
                    {d.date_remise || new Date(d.created_at).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-900 text-right font-mono">
                    {formatMoney(d.total_ht)}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-900 text-right font-mono font-medium">
                    {formatMoney(d.total_ttc)}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <Link
                        to={`/devis/${d.id}`}
                        data-testid={`view-devis-${d.id}`}
                        className="p-2 text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={() => handleDeleteDevis(d.id, d.objet)}
                        data-testid={`delete-devis-${d.id}`}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-md transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4" data-testid="new-devis-modal">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">Nouveau devis</h2>
              <button 
                onClick={() => setShowModal(false)}
                className="p-1 hover:bg-slate-100 rounded text-slate-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleCreateDevis} className="p-6 space-y-4">
              {clients.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-slate-500 mb-4">Vous devez d'abord créer un client</p>
                  <Link
                    to="/clients"
                    className="inline-flex items-center text-orange-500 hover:text-orange-600"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Créer un client
                  </Link>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Client *
                    </label>
                    <select
                      data-testid="select-client-input"
                      value={newDevis.client_id}
                      onChange={(e) => setNewDevis({...newDevis, client_id: e.target.value})}
                      className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                      required
                    >
                      <option value="">Sélectionner un client</option>
                      {clients.map(c => (
                        <option key={c.id} value={c.id}>{c.nom}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Objet du devis *
                    </label>
                    <input
                      data-testid="devis-objet-input"
                      type="text"
                      value={newDevis.objet}
                      onChange={(e) => setNewDevis({...newDevis, objet: e.target.value})}
                      placeholder="Ex: Rénovation magasin Lyon"
                      className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Date de remise
                      </label>
                      <input
                        data-testid="devis-date-input"
                        type="date"
                        value={newDevis.date_remise}
                        onChange={(e) => setNewDevis({...newDevis, date_remise: e.target.value})}
                        className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Version
                      </label>
                      <input
                        type="text"
                        value={newDevis.version}
                        onChange={(e) => setNewDevis({...newDevis, version: e.target.value})}
                        className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="flex-1 h-10 border border-slate-300 text-slate-700 rounded-md font-medium hover:bg-slate-50 transition-colors"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      data-testid="create-devis-btn"
                      className="flex-1 h-10 bg-slate-900 text-white rounded-md font-medium hover:bg-slate-800 transition-colors"
                    >
                      Créer le devis
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DevisListPage;
